import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { handleChunkLoadError } from 'app/services/chunk-error-handler';
import type { AppConfig } from './app/config/app.config';

export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
	'Refiner client',
	// window.webkit.messageHandlers is a WKWebView native bridge injected by the OS/host app,
	// not by our code. When a kiosk device's wrapper doesn't register the bridge, some
	// external/injected script's postMessage call throws here — outside app code, unfixable on our end.
	'window.webkit.messageHandlers',
	// MatIconRegistry reports every failed SVG fetch through ErrorHandler. Status 0 means the request never
	// got a response (offline, flaky Wi-Fi, page unloading), so there is nothing to fix. Other failures,
	// such as a 404 or a non-SVG body, still get reported.
	/^Error retrieving icon \S*:\S+! Http failure response for \S+: 0 Unknown Error$/,
];

/**
 * Initializes Sentry for error tracking and performance monitoring.
 *
 * Sentry is always initialized to ensure beforeSend hooks run (for chunk error handling).
 * The `enabled` flag controls whether events are actually sent to Sentry.
 *
 * @param config Runtime application configuration
 */
export function initSentry(config: AppConfig): void {
	const isEnabled = config.monitoring.sentry.enabled;

	Sentry.init({
		dsn: 'https://bd1a607220844e7b84077cf4694b8570@o4505053464494080.ingest.sentry.io/4505053466918912',
		enabled: isEnabled,
		debug: false, // Set to true to show Sentry debug logs in console
		environment: config.monitoring.sentry.environmentName,
		release: window.__BUILD_META__?.sentryRelease,
		replaysSessionSampleRate: isEnabled ? 0.01 : 0,
		replaysOnErrorSampleRate: isEnabled ? 0.01 : 0,
		tracesSampler: (samplingContext) => {
			if (!isEnabled) return 0;
			const name = samplingContext.name ?? '';
			// Sample 10% of pageloads on the passes routes for LCP measurement
			if (name.startsWith('/main/student/passes') || name.startsWith('/main/passes')) {
				return 0.1;
			}
			return 0.01;
		},
		integrations: [Sentry.browserTracingIntegration()],
		ignoreErrors: SENTRY_IGNORE_ERRORS,
		beforeSend(event, hint) {
			const exception = hint.originalException;
			console.debug('Sentry beforeSend', event, hint);

			const exceptionRecord = exception && typeof exception === 'object' ? (exception as Record<string, unknown>) : null;

			if (
				exception instanceof HttpErrorResponse ||
				exceptionRecord?.rejection instanceof HttpErrorResponse ||
				exceptionRecord?.name === 'HttpErrorResponse'
			) {
				return null;
			}
			if (event instanceof HttpErrorResponse) {
				return null;
			}

			if (event.exception?.values && event.exception.values.length > 0) {
				const ex: Sentry.Exception = event.exception.values[0];

				// Firebase
				if (ex.type === 'FirebaseError' && ex.value?.includes('messaging/unsupported-browser')) {
					console.log('Discarding firebase messaging error');
					return null;
				}

				// Bad HTTP responses
				if (ex.type === 'Error') {
					const serialized = event.extra?.__serialized__;
					if (serialized && typeof serialized === 'object' && 'name' in serialized && serialized.name === 'HttpErrorResponse') {
						return null;
					}
				}
			}

			if (handleChunkLoadError(hint.originalException)) {
				return null;
			}

			return event;
		},
	});

	if (isEnabled) {
		void import('@sentry/browser')
			.then(({ replayIntegration }) => {
				Sentry.addIntegration(
					replayIntegration({
						networkDetailAllowUrls: [window.location.origin],
						maskAllText: false,
						blockAllMedia: false,
					})
				);
			})
			.catch((error) => console.warn('Failed to load Sentry Replay:', error));
	} else {
		console.log('[Sentry] Initialized in disabled mode (beforeSend hooks active, events not sent)');
	}
}
